import { StandardEvents } from '@shopify/events';

/**
 * Unhooked Co — free-delivery progress bar for the cart drawer.
 *
 * The theme's drawer has no progress bar, so this injects one at the top of
 * the items list and keeps it in step with the cart. Everything else in the
 * drawer is the theme's own markup, restyled by unhook-cart.css.
 *
 * Threshold is read from a data attribute so it stays a theme setting rather
 * than a number buried in a script:
 *
 *   <body data-free-shipping="3000">   (minor units, so 3000 = £30.00)
 *
 * With no attribute the bar simply never renders.
 */

/* settings come from Theme settings > Cart > Free delivery bar, handed over
   by snippets/unhook-cart-config.liquid */
const CONFIG = (() => {
  const fallback = {
    enabled: false,
    threshold: 0,
    currency: 'GBP',
    textAway: "You're [amount] away from free UK delivery",
    textEmpty: 'Spend [amount] for free UK delivery',
    textDone: 'Free UK delivery unlocked',
  };
  const node = document.querySelector('[data-unhook-cart-config]');
  if (!node) return fallback;
  try {
    return { ...fallback, ...JSON.parse(node.textContent) };
  } catch {
    return fallback;
  }
})();

const THRESHOLD = CONFIG.enabled ? Number(CONFIG.threshold || 0) : 0;

/* Theme is declared with const in a classic script, so it lives in the global
   lexical scope rather than on window. Read it bare, and fall back for safety. */
const cartUrl = () => {
  try {
    return `${Theme.routes.cart_url}.js`;
  } catch {
    return '/cart.js';
  }
};

const money = (cents) => {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: CONFIG.currency || 'GBP',
    }).format(cents / 100);
  } catch {
    return `£${(cents / 100).toFixed(2)}`;
  }
};

const fill = (template, amount) => String(template || '').replace(/\[amount\]/g, `<b>${money(amount)}</b>`);

const TICK = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5L19 7"/></svg>`;

/** Paid goods only — a £0.00 gift should not push anyone over the line. */
const paidTotal = (cart) =>
  (cart.items || []).reduce((total, item) => total + (item.original_line_price > 0 ? item.final_line_price : 0), 0);

function markup(total) {
  const pct = Math.min(100, (total / THRESHOLD) * 100);
  const message =
    total >= THRESHOLD
      ? `${TICK}<b>${CONFIG.textDone}</b>`
      : total > 0
        ? fill(CONFIG.textAway, THRESHOLD - total)
        : fill(CONFIG.textEmpty, THRESHOLD);
  return `<div class="uh-ship" data-uh-ship>
      <p>${message}</p>
      <div class="uh-ship__track"><div class="uh-ship__fill" style="width:${pct}%"></div></div>
    </div>`;
}

const GIFT_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 11h18v9H3zM3 7h18v4H3z"/><path d="M12 7v13M12 7S9.5 3 7.5 4.2 9 7 12 7zM12 7s2.5-4 4.5-2.8S15 7 12 7z"/></svg>`;

/**
 * The gifts strip and the savings line, both injected above the totals.
 * The theme prints neither, and both are in the design.
 */
function summaryExtras(drawer, cart) {
  const totals = drawer.querySelector('.cart-totals');
  if (!totals) return;

  /* gifts: the £0.00 lines already in the cart, named rather than assumed */
  const gifts = (cart.items || []).filter((i) => i.original_line_price === 0);
  drawer.querySelector('[data-uh-gifts]')?.remove();
  if (gifts.length) {
    const names = gifts.map((g) => g.product_title).join(' · ');
    const worth = gifts.reduce((t, g) => t + (g.variant?.compare_at_price || 0) * g.quantity, 0);
    totals.insertAdjacentHTML(
      'beforebegin',
      `<p class="uh-gifts" data-uh-gifts>${GIFT_ICON}
         <span><b>${gifts.length} free gift${gifts.length > 1 ? 's' : ''} included</b><small>${names}</small></span>
         ${worth > 0 ? `<em>Worth ${money(worth)}</em>` : ''}
       </p>`
    );
  }

  /* savings: compare-at minus what is actually being charged */
  const saving = (cart.items || []).reduce((total, i) => {
    const was = (i.variant?.compare_at_price || i.original_price) * i.quantity;
    return total + Math.max(0, was - i.final_line_price);
  }, 0);
  drawer.querySelector('[data-uh-saving]')?.remove();
  if (saving > 0) {
    totals.insertAdjacentHTML(
      'afterend',
      `<p class="uh-saving" data-uh-saving>You're saving ${money(saving)}</p>`
    );
  }
}

async function refresh() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;

  /* nothing to say when the cart is empty — the empty state speaks for itself */
  const host = drawer.querySelector('.cart-drawer__items') || drawer.querySelector('.cart-drawer__content');
  if (!host || drawer.querySelector('.cart-drawer--empty')) {
    drawer.querySelector('[data-uh-ship]')?.remove();
    return;
  }

  let cart;
  try {
    cart = await fetch(cartUrl(), { credentials: 'same-origin' }).then((r) => r.json());
  } catch {
    return;
  }
  if (!cart.items?.length) {
    drawer.querySelector('[data-uh-ship]')?.remove();
    return;
  }

  if (!THRESHOLD) {
    summaryExtras(drawer, cart);
    return;
  }

  const html = markup(paidTotal(cart));
  const existing = drawer.querySelector('[data-uh-ship]');
  if (existing) existing.outerHTML = html;
  else host.insertAdjacentHTML('beforebegin', html);

  summaryExtras(drawer, cart);
}

document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  /* the drawer re-renders its section after the event, so wait a frame */
  requestAnimationFrame(() => setTimeout(refresh, 60));
});
document.addEventListener('DOMContentLoaded', refresh);
refresh();